import React, { useEffect, useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { selectIsSubscriptionRestricted } from './app/auth/authSlice';
import useDarkMode from './hooks/useDarkMode';

import { AppSettings } from './config/app-settings.js';
import { slideToggle } from './composables/slideToggle.js';
import { useLocation } from 'react-router-dom';
import Header from './components/header/header.jsx';
import Sidebar from './components/sidebar/sidebar.jsx';
import SidebarRight from './components/sidebar-right/sidebar-right.jsx';
import TopMenu from './components/top-menu/top-menu.jsx';
import Content from './components/content/content.jsx';
import ThemePanel from './components/theme-panel/theme-panel.jsx';
import TokenExpiryChecker from './components/CustomComponents/TokenExpiryChecker/TokenExpiryChecker.jsx';
import SubscriptionNoticeBar from './components/SubscriptionNoticeBar/SubscriptionNoticeBar.jsx';
import DynamicTitle from './components/DynamicTitle/DynamicTitle.jsx';
import useUserDataRefresh from './hooks/useUserDataRefresh.js';
import useGlobalThemeColors from './hooks/useGlobalThemeColors.js';
import {
  setAppTheme,
  setAppDarkMode,
  setAppGradientEnabled,
  setAppHeaderFixed,
  setAppHeaderInverse,
  setAppSidebarFixed,
  setAppSidebarGrid,
} from './app/appSettingsSlice.js';
 
function App() {	
	const location = useLocation();
	const dispatch = useDispatch();
	const isSubscriptionRestricted = useSelector(selectIsSubscriptionRestricted);
	
	// Get app settings from Redux
	const appSettings = useSelector((state) => state.appSettings);
	const {
		appTheme,
		appDarkMode,
		appGradientEnabled,
		appHeaderFixed,
		appHeaderInverse,
		appSidebarFixed,
		appSidebarGrid,
	} = appSettings;
	
	// Initialize user data auto-refresh
	useUserDataRefresh();
	
	// Set global CSS variables for theme colors
	useGlobalThemeColors();
	
	// Global ResizeObserver error handler
	useEffect(() => {
		// Override ResizeObserver globally to suppress the error
		const OriginalResizeObserver = window.ResizeObserver;
		window.ResizeObserver = class extends OriginalResizeObserver {
			constructor(callback) {
				super((entries, observer) => {
					try {
						callback(entries, observer);
					} catch (error) {
						if (error.message && error.message.includes('ResizeObserver')) {
							// Suppress ResizeObserver errors
							return;
						}
						throw error;
					}
				});
			}
		};

		const handleResizeObserverError = (e) => {
			if (
				e.message ===
				"ResizeObserver loop completed with undelivered notifications."
			) {
				e.stopImmediatePropagation();
				e.preventDefault();
				return false;
			}
		};

		const handleUnhandledRejection = (e) => {
			if (
				e.reason && 
				e.reason.message && 
				e.reason.message.includes("ResizeObserver")
			) {
				e.preventDefault();
				return false;
			}
		};

		// Add multiple event listeners to catch the error at different levels
		window.addEventListener("error", handleResizeObserverError, true);
		window.addEventListener("unhandledrejection", handleUnhandledRejection, true);
		
		// Also override console.error to suppress ResizeObserver errors
		const originalConsoleError = console.error;
		console.error = (...args) => {
			if (args[0] && typeof args[0] === 'string' && args[0].includes('ResizeObserver')) {
				return; // Suppress ResizeObserver console errors
			}
			originalConsoleError.apply(console, args);
		};

		return () => {
			// Restore original ResizeObserver
			window.ResizeObserver = OriginalResizeObserver;
			window.removeEventListener("error", handleResizeObserverError, true);
			window.removeEventListener("unhandledrejection", handleUnhandledRejection, true);
			console.error = originalConsoleError;
		};
	}, []);
	
	// Local state for UI-specific settings not in Redux
	const [appHeaderNone, setAppHeaderNone] = useState(false);
	const [appHeaderMegaMenu, setAppHeaderMegaMenu] = useState(false);
	const [appHeaderLanguageBar, setAppHeaderLanguageBar] = useState(false);
	const [hasScroll, setHasScroll] = useState(false);
	const [appSidebarNone, setAppSidebarNone] = useState(false);
	const [appSidebarWide, setAppSidebarWide] = useState(false);
	const [appSidebarLight, setAppSidebarLight] = useState(false);
	const [appSidebarMinify, setAppSidebarMinify] = useState(false);
	const [appSidebarMobileToggled, setAppSidebarMobileToggled] = useState(false);
	const [appSidebarTransparent, setAppSidebarTransparent] = useState(false);
	const [appSidebarSearch, setAppSidebarSearch] = useState(false);
	const [appContentNone, setAppContentNone] = useState(false);
	const [appContentClass, setAppContentClass] = useState('');
	const [appContentFullHeight, setAppContentFullHeight] = useState(false);
	const [appTopMenu, setAppTopMenu] = useState(false);
	const [appTopMenuMobileToggled] = useState(false);
	const [appSidebarTwo, setAppSidebarTwo] = useState(false);
	const [appSidebarEnd, setAppSidebarEnd] = useState(false);
	const [appSidebarEndToggled, setAppSidebarEndToggled] = useState(false);
	const [appSidebarEndMobileToggled, setAppSidebarEndMobileToggled] = useState(false);

	// Initialize theme and dark mode from Redux state
	useEffect(() => {
		// Set theme
		if (appTheme) {
			const newTheme = 'theme-' + appTheme;
			for (var x = 0; x < document.body.classList.length; x++) {
				if (
					document.body.classList[x].indexOf('theme-') > -1 &&
					document.body.classList[x] !== newTheme
				) {
					document.body.classList.remove(document.body.classList[x]);
				}
			}
			document.body.classList.add(newTheme);
		}

		// Set dark mode
		if (appDarkMode) {
			document.querySelector('html').setAttribute('data-bs-theme', 'dark');
		} else {
			document.querySelector('html').removeAttribute('data-bs-theme');
		}
	}, [appTheme, appDarkMode]);

	// Check if we're on the login page or auto-login page
	const isOnLoginPage = location.pathname.includes('/user/login');
	const isOnAutoLoginPage = location.pathname.includes('/auto-login');
	const isBillingOnlyMode =
		isSubscriptionRestricted && !isOnLoginPage && !isOnAutoLoginPage;
	const hideAppChrome = isOnLoginPage || isOnAutoLoginPage || isBillingOnlyMode;
	
	// Hide chrome on login/auto-login and when subscription grace has ended (billing-only).
	useEffect(() => {
		if (hideAppChrome) {
			setAppSidebarNone(true);
			setAppHeaderNone(true);
			setAppContentClass(isOnLoginPage || isOnAutoLoginPage ? "p-0" : "");
		} else {
			setAppSidebarNone(false);
			setAppHeaderNone(false);
			setAppContentClass("");
		}
	}, [hideAppChrome, isOnLoginPage, isOnAutoLoginPage]);

	// Function to conditionally render components
	const renderComponents = () => {
		if (isOnLoginPage || isOnAutoLoginPage) {
			return (
				<>
					<DynamicTitle />
					{!appContentNone && <Content />}
				</>
			);
		}

		if (isBillingOnlyMode) {
			return (
				<>
					<DynamicTitle />
					{!appContentNone && <Content />}
					<TokenExpiryChecker />
				</>
			);
		}
		
		return (
			<>
				<DynamicTitle />
				{!hideAppChrome && !appHeaderNone && <Header />}
				{!hideAppChrome && !appHeaderNone && <SubscriptionNoticeBar />}
				{!hideAppChrome && !appSidebarNone && <Sidebar />}
				{appSidebarTwo && <SidebarRight />}
				{appTopMenu && <TopMenu />}
				{!appContentNone && <Content />}
				<ThemePanel />
				<TokenExpiryChecker />
			</>
		);
	};
  
  const handleSetAppHeaderNone = (value) => {
		setAppHeaderNone(value);
	};

	const handleSetAppHeaderInverse = (value) => {
		dispatch(setAppHeaderInverse(value));
	};

	const handleSetAppHeaderLanguageBar = (value) => {
		setAppHeaderLanguageBar(value);
	};

	const handleSetAppHeaderMegaMenu = (value) => {
		setAppHeaderMegaMenu(value);
	};

	const handleSetAppHeaderFixed = (value) => {
		if (value === false && appSidebarFixed) {
			alert('Default Header with Fixed Sidebar option is not supported. Proceed with Default Header with Default Sidebar.');
			dispatch(setAppSidebarFixed(false));
		}
		dispatch(setAppHeaderFixed(value));
	};

	const handleSetAppSidebarNone = (value) => {
		setAppSidebarNone(value);
	};

	const handleSetAppSidebarWide = (value) => {
		setAppSidebarWide(value);
	};

	const handleSetAppSidebarLight = (value) => {
		setAppSidebarLight(value);
	};

	const handleSetAppSidebarMinified = (value) => {
		setAppSidebarMinify(value);
	};

	const handleSetAppSidebarTransparent = (value) => {
		setAppSidebarTransparent(value);
	};

	const handleSetAppSidebarSearch = (value) => {
		setAppSidebarSearch(value);
	};

	const handleSetAppSidebarFixed = (value) => {
		if (value === true && !appHeaderFixed) {
			alert('Default Header with Fixed Sidebar option is not supported. Proceed with Fixed Header with Fixed Sidebar.');
			dispatch(setAppHeaderFixed(true));
		}
		dispatch(setAppSidebarFixed(value));
	};

	const handleSetAppSidebarGrid = (value) => {
		dispatch(setAppSidebarGrid(value));
	};

	const toggleAppSidebarMinify = (e) => {
		e.preventDefault();
		setAppSidebarMinify(!appSidebarMinify);
		if (localStorage) {
			localStorage.appSidebarMinify = !appSidebarMinify;
		}
	};

	const toggleAppSidebarMobile = (e) => {
		e.preventDefault();
		setAppSidebarMobileToggled(!appSidebarMobileToggled);
	};

	const handleSetAppSidebarEnd = (value) => {
		setAppSidebarEnd(value);
	};

	const handleSetAppContentNone = (value) => {
		setAppContentNone(value);
	};

	const handleSetAppContentClass = (value) => {
		setAppContentClass(value);
	};

	const handleSetAppContentFullHeight = (value) => {
		setAppContentFullHeight(value);
	};

	const handleSetAppTopMenu = (value) => {
		setAppTopMenu(value);
	};

	const toggleAppTopMenuMobile = (e) => {
		e.preventDefault();
		slideToggle(document.querySelector('.app-top-menu'));
	};

	const handleSetAppSidebarTwo = (value) => {
		setAppSidebarTwo(value);
		setAppSidebarEndToggled(value);
	};

	const handleSetAppBoxedLayout = (value) => {
		if (value === true) {
			document.body.classList.add('boxed-layout');
		} else {
			document.body.classList.remove('boxed-layout');
		}
	};

	const handleSetAppDarkMode = (value) => {
		if (value === true) {
			document.querySelector('html').setAttribute('data-bs-theme', 'dark');
		} else {
			document.querySelector('html').removeAttribute('data-bs-theme');
		}
		dispatch(setAppDarkMode(value));
		document.dispatchEvent(new Event('theme-reload'));
	};

	const handleSetAppGradientEnabled = (value) => {
		dispatch(setAppGradientEnabled(value));
	};

	const handleSetAppTheme = (value) => {
		var newTheme = 'theme-' + value;
		for (var x = 0; x < document.body.classList.length; x++) {
			if (
				document.body.classList[x].indexOf('theme-') > -1 &&
				document.body.classList[x] !== newTheme
			) {
				document.body.classList.remove(document.body.classList[x]);
			}
		}
		document.body.classList.add(newTheme);

		dispatch(setAppTheme(value));
		document.dispatchEvent(new Event('theme-reload'));
	};
	
	const toggleAppSidebarEnd = (e) => {
		e.preventDefault();
		setAppSidebarEndToggled(!appSidebarEndToggled);
	};
	
	const toggleAppSidebarEndMobile = (e) => {
		e.preventDefault();
		setAppSidebarEndMobileToggled(!appSidebarEndMobileToggled);
	}

	useEffect(() => {
		handleSetAppTheme(appTheme);
		if (appDarkMode) {
			handleSetAppDarkMode(true);
		}

		const handleScroll = () => {
			if (window.scrollY > 0) {
				setHasScroll(true);
			} else {
				setHasScroll(false);
			}
			var elm = document.getElementsByClassName('nvtooltip');
			for (var i = 0; i < elm.length; i++) {
				elm[i].classList.add('d-none');
			}
		};

		window.addEventListener('scroll', handleScroll);

		return () => {
			window.removeEventListener('scroll', handleScroll);
		};
	}, [appTheme, appDarkMode]);
 
	return (
		<AppSettings.Provider
			value={{
				appTheme,
				appDarkMode,
				appGradientEnabled,
				appHeaderNone,
				appHeaderFixed,
				appHeaderInverse,
				appHeaderMegaMenu,
				appHeaderLanguageBar,
				hasScroll,
				handleSetAppHeaderNone,
				handleSetAppHeaderInverse,
				handleSetAppHeaderLanguageBar,
				handleSetAppHeaderMegaMenu,
				handleSetAppHeaderFixed,
				appSidebarNone,
				appSidebarWide,
				appSidebarLight,
				appSidebarMinify,
				appSidebarMobileToggled,
				appSidebarTransparent,
				appSidebarSearch,
				appSidebarFixed,
				appSidebarGrid,
				handleSetAppSidebarNone,
				handleSetAppSidebarWide,
				handleSetAppSidebarLight,
				handleSetAppSidebarMinified,
				handleSetAppSidebarTransparent,
				handleSetAppSidebarSearch,
				handleSetAppSidebarFixed,
				handleSetAppSidebarGrid,
				toggleAppSidebarMinify,
				toggleAppSidebarMobile,
				appContentNone,
				appContentClass,
				appContentFullHeight,
				handleSetAppContentNone,
				handleSetAppContentClass,
				handleSetAppContentFullHeight,
				appTopMenu,
				appTopMenuMobileToggled,
				toggleAppTopMenuMobile,
				handleSetAppTopMenu,
				appSidebarTwo,
				handleSetAppSidebarTwo,
				appSidebarEnd,
				appSidebarEndToggled,
				appSidebarEndMobileToggled,
				toggleAppSidebarEnd,
				toggleAppSidebarEndMobile,
				handleSetAppSidebarEnd,
				handleSetAppBoxedLayout,
				handleSetAppDarkMode,
				handleSetAppGradientEnabled,
				handleSetAppTheme,
			}}
		>
			<div
				className={
					'app ' +
					(appGradientEnabled ? 'app-gradient-enabled ' : '') +
					(appHeaderNone || hideAppChrome ? 'app-without-header ' : '') +
					(appHeaderFixed && !appHeaderNone && !hideAppChrome ? 'app-header-fixed ' : '') +
					(appSidebarFixed ? 'app-sidebar-fixed ' : '') +
					(appSidebarNone || hideAppChrome ? 'app-without-sidebar ' : '') +
					(appSidebarEnd ? 'app-with-end-sidebar ' : '') +
					(appSidebarWide ? 'app-with-wide-sidebar ' : '') +
					(appSidebarMinify ? 'app-sidebar-minified ' : '') +
					(appSidebarMobileToggled ? 'app-sidebar-mobile-toggled ' : '') +
					(appTopMenu ? 'app-with-top-menu ' : '') +
					(appContentFullHeight ? 'app-content-full-height ' : '') +
					(appSidebarTwo ? 'app-with-two-sidebar ' : '') +
					(appSidebarEndToggled ? 'app-sidebar-end-toggled ' : '') +
					(appSidebarEndMobileToggled ? 'app-sidebar-end-mobile-toggled ' : '') +
					(hasScroll ? 'has-scroll ' : '') +
					(isSubscriptionRestricted ? 'subscription-restricted ' : '')
				}
			>
				{renderComponents()}
			</div>
		</AppSettings.Provider>
	);
}

export default App;